import { formatCurrency, resolveMoney } from '@/utils/formatCurrency';
import {
  parseCampaignPlacementBidPercents,
  type CampaignPlacementBidKey,
  type CampaignPlacementBidPercents,
} from '@/utils/workspaceCampaignPlacement';
import {
  formatEnteredProductLookupMessage,
  parseAsinList,
  parseKeywordList,
  resolveEnteredAdvertisedProducts,
  validateKeywordBids,
  validateProductTargetBids,
} from '@/utils/spCampaignCreateDraft';
import {
  SP_BIDDING_STRATEGIES,
  type SpAddedNegativeBrand,
  type SpAddedNegativeKeyword,
  type SpAddedNegativeProduct,
  type SpAddedKeyword,
  type SpNegativeMatchType,
  type SpAddedProductTarget,
  type SpAdvertisedProduct,
  type SpBiddingStrategy,
  type SpManualTargetingKind,
} from '@/types/spCampaignCreate';

export type SpAutoTargetingMode = 'default' | 'custom';

export type SpAutoTargetingGroupId = 'close' | 'loose' | 'substitutes' | 'complements';

export type SpAutoTargetingGroupDef = {
  id: SpAutoTargetingGroupId;
  label: string;
  expressionType: string;
};

export const SP_AUTO_TARGETING_GROUPS: readonly SpAutoTargetingGroupDef[] = [
  { id: 'close', label: 'Close match', expressionType: 'QUERY_HIGH_REL_MATCHES' },
  { id: 'loose', label: 'Loose match', expressionType: 'QUERY_BROAD_REL_MATCHES' },
  { id: 'substitutes', label: 'Substitutes', expressionType: 'ASIN_SUBSTITUTE_RELATED' },
  { id: 'complements', label: 'Complements', expressionType: 'ASIN_ACCESSORY_RELATED' },
];

export type SpAutoTargetingGroupState = {
  id: SpAutoTargetingGroupId;
  enabled: boolean;
  bid: string;
};

export function defaultAutoTargetingGroups(defaultBid: string): SpAutoTargetingGroupState[] {
  return SP_AUTO_TARGETING_GROUPS.map((group) => ({
    id: group.id,
    enabled: true,
    bid: defaultBid,
  }));
}

export function seedAutoTargetingGroupBids(
  groups: SpAutoTargetingGroupState[],
  previousDefaultBid: string,
  nextDefaultBid: string
): SpAutoTargetingGroupState[] {
  return groups.map((group) =>
    group.bid.trim() === previousDefaultBid.trim() ? { ...group, bid: nextDefaultBid } : group
  );
}

export function spCampaignTargetingType(campaign: {
  adProduct?: string;
  raw?: Record<string, unknown>;
} | null | undefined): 'AUTO' | 'MANUAL' | null {
  if (!campaign || campaign.adProduct !== 'SP') return null;
  const targetingType = String(
    campaign.raw?.targetingType ?? campaign.raw?.targeting_type ?? ''
  ).toUpperCase();
  if (targetingType === 'AUTO' || targetingType === 'MANUAL') return targetingType;
  return null;
}

export function spCampaignTargetingTypeLabel(campaign: {
  adProduct?: string;
  raw?: Record<string, unknown>;
} | null | undefined): string {
  const targetingType = spCampaignTargetingType(campaign);
  if (targetingType === 'AUTO') return 'Automatic';
  if (targetingType === 'MANUAL') return 'Manual';
  return 'N/A';
}

export function isSpAutoCampaign(campaign: {
  adProduct?: string;
  raw?: Record<string, unknown>;
} | null | undefined): boolean {
  return spCampaignTargetingType(campaign) === 'AUTO';
}

export function isSpManualCampaign(campaign: {
  adProduct?: string;
  raw?: Record<string, unknown>;
} | null | undefined): boolean {
  return spCampaignTargetingType(campaign) === 'MANUAL';
}

export function canCreateSpAdGroup(campaign: {
  adProduct?: string;
  raw?: Record<string, unknown>;
} | null | undefined): boolean {
  return isSpAutoCampaign(campaign) || isSpManualCampaign(campaign);
}

export function campaignBiddingStrategyFromRaw(
  raw: Record<string, unknown> | undefined
): SpBiddingStrategy | null {
  if (!raw) return null;
  const dynamic =
    raw.dynamicBidding && typeof raw.dynamicBidding === 'object'
      ? (raw.dynamicBidding as Record<string, unknown>)
      : raw.bidding && typeof raw.bidding === 'object'
        ? (raw.bidding as Record<string, unknown>)
        : null;
  const strategy = String(dynamic?.strategy ?? '').toUpperCase();
  if (strategy === 'LEGACY_FOR_SALES' || strategy === 'AUTO_FOR_SALES' || strategy === 'MANUAL') {
    return strategy;
  }
  return null;
}

export function campaignBiddingStrategyCopy(strategy: SpBiddingStrategy | null): {
  label: string;
  description: string;
} {
  if (strategy === 'LEGACY_FOR_SALES') {
    return {
      label: 'Dynamic bids – down only',
      description: "We'll lower your bids in real time when your ad may be less likely to convert to a sale.",
    };
  }
  if (strategy === 'AUTO_FOR_SALES') {
    return {
      label: 'Dynamic bids – up and down',
      description:
        "We'll raise your bids (by a maximum of 100% on all placements) in real time when your ad may be more likely to convert to a sale, and lower your bids when less likely to convert to a sale.",
    };
  }
  const match = SP_BIDDING_STRATEGIES.find((option) => option.value === strategy);
  if (match) return { label: match.label, description: match.description };
  return {
    label: strategy ? strategy.replace(/_/g, ' ') : 'Not set on this campaign',
    description: 'This campaign does not expose a bidding strategy we can display.',
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readPercentage(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export const CAMPAIGN_BID_ADJUSTMENT_PLACEMENTS: Array<{
  id: CampaignPlacementBidKey;
  label: string;
  summaryLabel: string;
}> = [
  { id: 'top_of_search', label: 'Top of search (first page)', summaryLabel: 'Top of search (first page)' },
  { id: 'rest_of_search', label: 'Rest of search', summaryLabel: 'Rest of search' },
  { id: 'product_pages', label: 'Product pages', summaryLabel: 'Product pages' },
];

export type CampaignAudienceBidAdjustment = {
  audienceId: string;
  percentage: number;
};

export function displayBidPercent(value: number | null | undefined): string {
  return `${value ?? 0}%`;
}

export function signedBidPercent(value: number): string {
  if (value > 0) return `+${value}%`;
  return `${value}%`;
}

export function appliedCampaignPlacementAdjustments(
  percents: CampaignPlacementBidPercents
): Array<{ id: CampaignPlacementBidKey; label: string; percentage: number }> {
  return CAMPAIGN_BID_ADJUSTMENT_PLACEMENTS.flatMap((row) => {
    const percentage = percents[row.id];
    if (percentage == null || percentage <= 0) return [];
    return [{ id: row.id, label: row.summaryLabel, percentage }];
  });
}

export function parseAmazonBusinessBidPercent(
  raw: Record<string, unknown> | undefined
): number | null {
  const dynamic = asRecord(raw?.dynamicBidding) ?? asRecord(raw?.dynamic_bidding);
  if (!dynamic) return null;
  for (const item of asList(dynamic.placementBidding)) {
    const rec = asRecord(item);
    if (!rec) continue;
    const token = String(rec.placement ?? '').toUpperCase();
    if (!token.includes('BUSINESS')) continue;
    return readPercentage(rec.percentage);
  }
  for (const item of asList(dynamic.shopperCohortBidding)) {
    const rec = asRecord(item);
    if (!rec) continue;
    const type = String(rec.shopperCohortType ?? '').toUpperCase();
    if (!type.includes('BUSINESS')) continue;
    return readPercentage(rec.percentage);
  }
  return null;
}

export function parseCampaignAudienceBidAdjustments(
  raw: Record<string, unknown> | undefined
): CampaignAudienceBidAdjustment[] {
  const rows: CampaignAudienceBidAdjustment[] = [];
  const seen = new Set<string>();
  const add = (audienceId: string, percentage: number) => {
    const id = audienceId.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    rows.push({ audienceId: id, percentage });
  };

  const dynamic = asRecord(raw?.dynamicBidding) ?? asRecord(raw?.dynamic_bidding);
  if (dynamic) {
    for (const item of asList(dynamic.shopperCohortBidding)) {
      const rec = asRecord(item);
      if (!rec) continue;
      const type = String(rec.shopperCohortType ?? '').toUpperCase();
      if (type.includes('BUSINESS')) continue;
      const percentage = readPercentage(rec.percentage);
      if (percentage == null) continue;
      const segments = asList(rec.audienceSegments);
      if (segments.length === 0) {
        add(type || 'Audience', percentage);
        continue;
      }
      for (const segment of segments) {
        const seg = asRecord(segment);
        add(String(seg?.audienceId ?? seg?.name ?? type ?? 'Audience'), percentage);
      }
    }
    const bidAdjustments = asRecord(dynamic.bidAdjustments);
    for (const item of asList(bidAdjustments?.audienceBidAdjustments)) {
      const rec = asRecord(item);
      if (!rec) continue;
      const percentage = readPercentage(rec.percentage);
      if (percentage == null) continue;
      add(String(rec.audienceId ?? ''), percentage);
    }
  }
  return rows;
}

function formatBidAmount(value: number): string {
  if (!resolveMoney()) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return formatCurrency(value, undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatCampaignPlacementPreview(
  defaultBid: string,
  percents: CampaignPlacementBidPercents
): string {
  const bid = parseFloat(defaultBid);
  const base = Number.isFinite(bid) && bid > 0 ? bid : 0;
  const amount = (key: CampaignPlacementBidKey) =>
    formatBidAmount(base * (1 + (percents[key] ?? 0) / 100));
  return `A ${formatBidAmount(base)} bid will start at ${amount('top_of_search')} for top of search, ${amount('rest_of_search')} for rest of search and ${amount('product_pages')} for product pages.`;
}

export function campaignPlacementBidPercentsFromRaw(
  raw: Record<string, unknown> | undefined
): CampaignPlacementBidPercents {
  return parseCampaignPlacementBidPercents(raw);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function pad3(value: number): string {
  return String(value).padStart(3, '0');
}

/** Name filled when the user clicks New ad group: "Ad group - DD/MM/YYYY HH:MM:SS.mmm". */
export function defaultSpAdGroupName(clickedAt: Date = new Date()): string {
  const date = `${pad2(clickedAt.getDate())}/${pad2(clickedAt.getMonth() + 1)}/${clickedAt.getFullYear()}`;
  const time = `${pad2(clickedAt.getHours())}:${pad2(clickedAt.getMinutes())}:${pad2(clickedAt.getSeconds())}.${pad3(clickedAt.getMilliseconds())}`;
  return `Ad group - ${date} ${time}`;
}

export function addTypedProductsFromText(
  raw: string,
  existing: SpAdvertisedProduct[],
  metadata: SpAdvertisedProduct[] = []
): {
  products: SpAdvertisedProduct[];
  message: string | null;
  ids: string[];
} {
  const ids = parseAsinList(raw);
  if (ids.length === 0) {
    return { products: existing, message: 'Enter at least one ASIN or SKU.', ids };
  }
  const result = resolveEnteredAdvertisedProducts({
    ids,
    metadata,
    catalog: [],
    existing,
  });
  return {
    products: result.toAdd.length > 0 ? [...existing, ...result.toAdd] : existing,
    message: formatEnteredProductLookupMessage(result),
    ids,
  };
}

export function addNegativeKeywordsFromText(
  raw: string,
  matchType: SpNegativeMatchType,
  existing: SpAddedNegativeKeyword[]
): { keywords: SpAddedNegativeKeyword[]; added: number } {
  const incoming = parseKeywordList(raw);
  const keys = new Set(existing.map((row) => `${row.keywordText.toLowerCase()}::${row.matchType}`));
  const keywords = [...existing];
  let added = 0;
  for (const keywordText of incoming) {
    const key = `${keywordText.toLowerCase()}::${matchType}`;
    if (keys.has(key)) continue;
    keys.add(key);
    keywords.push({ keywordText, matchType });
    added += 1;
  }
  return { keywords, added };
}

export function addNegativeProductsFromText(
  raw: string,
  existing: SpAddedNegativeProduct[]
): { products: SpAddedNegativeProduct[]; added: number } {
  const incoming = parseAsinList(raw);
  const keys = new Set(existing.map((row) => row.asin));
  const products = [...existing];
  let added = 0;
  for (const asin of incoming) {
    if (keys.has(asin)) continue;
    keys.add(asin);
    products.push({ asin });
    added += 1;
  }
  return { products, added };
}

export function addNegativeBrandsFromText(
  raw: string,
  existing: SpAddedNegativeBrand[]
): { brands: SpAddedNegativeBrand[]; added: number } {
  const incoming = parseKeywordList(raw);
  const keys = new Set(existing.map((row) => row.brand.toLowerCase()));
  const brands = [...existing];
  let added = 0;
  for (const brand of incoming) {
    const key = brand.toLowerCase();
    if (keys.has(key)) continue;
    keys.add(key);
    brands.push({ brand });
    added += 1;
  }
  return { brands, added };
}

export function buildNegativeTargetClauses(input: {
  campaignId: string;
  adGroupId?: string;
  products: SpAddedNegativeProduct[];
  brands?: SpAddedNegativeBrand[];
}): Array<Record<string, unknown>> {
  const scope = input.adGroupId
    ? { campaignId: input.campaignId, adGroupId: input.adGroupId }
    : { campaignId: input.campaignId };
  const products = input.products.map((row) => ({
    ...scope,
    state: 'ENABLED',
    expression: [{ type: 'ASIN_SAME_AS', value: row.asin }],
  }));
  const brands = (input.brands ?? []).map((row) => ({
    ...scope,
    state: 'ENABLED',
    expression: [{ type: 'ASIN_BRAND_SAME_AS', value: row.brand }],
  }));
  return [...products, ...brands];
}

export function validateSpAutoAdGroupForm(input: {
  name: string;
  defaultBid: string;
  products: SpAdvertisedProduct[];
  targetingMode: SpAutoTargetingMode;
  groups: SpAutoTargetingGroupState[];
}): string | null {
  if (!input.name.trim()) return 'Ad group name is required.';
  const defaultBid = parseFloat(input.defaultBid);
  if (Number.isNaN(defaultBid) || defaultBid <= 0) return 'Enter a default bid greater than 0.';
  if (input.products.length === 0) return 'Add at least one product.';
  if (input.targetingMode === 'custom') {
    const enabled = input.groups.filter((group) => group.enabled);
    if (enabled.length === 0) return 'Enable at least one targeting group.';
    for (const group of enabled) {
      const bid = parseFloat(group.bid);
      if (Number.isNaN(bid) || bid <= 0) {
        const def = SP_AUTO_TARGETING_GROUPS.find((item) => item.id === group.id);
        return `Enter a bid for ${def?.label ?? group.id}.`;
      }
    }
  }
  return null;
}

export function validateSpManualAdGroupForm(input: {
  name: string;
  defaultBid: string;
  products: SpAdvertisedProduct[];
  targetingKind: SpManualTargetingKind;
  keywords: SpAddedKeyword[];
  productTargets: SpAddedProductTarget[];
}): string | null {
  if (!input.name.trim()) return 'Ad group name is required.';
  const defaultBid = parseFloat(input.defaultBid);
  if (Number.isNaN(defaultBid) || defaultBid <= 0) return 'Enter a default bid greater than 0.';
  if (input.products.length === 0) return 'Add at least one product.';
  if (input.targetingKind === 'PRODUCT') return validateProductTargetBids(input.productTargets);
  return validateKeywordBids(input.keywords);
}

export function buildSpManualProductTargetPayloads(input: {
  campaignId: string;
  adGroupId: string;
  productTargets: SpAddedProductTarget[];
}): Array<Record<string, unknown>> {
  return input.productTargets.map((row) => ({
    campaignId: input.campaignId,
    adGroupId: input.adGroupId,
    expressionType: 'MANUAL',
    state: 'ENABLED',
    bid: parseFloat(row.bid),
    expression: [{ type: row.expressionType, value: row.value }],
  }));
}

export function buildSpKeywordPayloads(input: {
  campaignId: string;
  adGroupId: string;
  keywords: SpAddedKeyword[];
}): Array<Record<string, unknown>> {
  return input.keywords.map((row) => ({
    campaignId: input.campaignId,
    adGroupId: input.adGroupId,
    keywordText: row.keywordText,
    matchType: row.matchType,
    bid: parseFloat(row.bid),
    state: 'ENABLED',
  }));
}

/**
 * Amazon auto-creates the four AUTO expression clauses when the ad group is created.
 * Do not POST create for these — that returns errors like "product targeting is not
 * available for this expression to add manually". Use list + update instead.
 */
export function buildSpAutoTargetingClauses(input: {
  campaignId: string;
  adGroupId: string;
  targetingMode: SpAutoTargetingMode;
  defaultBid: number;
  groups: SpAutoTargetingGroupState[];
}): Array<Record<string, unknown>> {
  const defs = new Map(SP_AUTO_TARGETING_GROUPS.map((group) => [group.id, group]));
  const selected =
    input.targetingMode === 'default'
      ? SP_AUTO_TARGETING_GROUPS.map((group) => ({
          id: group.id,
          enabled: true,
          bid: String(input.defaultBid),
        }))
      : input.groups.filter((group) => group.enabled);

  return selected.flatMap((group) => {
    const def = defs.get(group.id);
    if (!def) return [];
    const bid =
      input.targetingMode === 'default' ? input.defaultBid : parseFloat(group.bid);
    return [
      {
        campaignId: input.campaignId,
        adGroupId: input.adGroupId,
        expressionType: 'AUTO',
        state: 'ENABLED',
        bid,
        expression: [{ type: def.expressionType }],
      },
    ];
  });
}

/** Map Amazon AUTO expression types (and Unified aliases) onto our group ids. */
const AUTO_EXPRESSION_TO_GROUP: Record<string, SpAutoTargetingGroupId> = {
  QUERY_HIGH_REL_MATCHES: 'close',
  KEYWORDS_CLOSE_MATCH: 'close',
  SEARCH_CLOSE_MATCH: 'close',
  CLOSE_MATCH: 'close',
  QUERY_BROAD_REL_MATCHES: 'loose',
  KEYWORDS_LOOSE_MATCH: 'loose',
  SEARCH_LOOSE_MATCH: 'loose',
  LOOSE_MATCH: 'loose',
  ASIN_SUBSTITUTE_RELATED: 'substitutes',
  PRODUCT_SUBSTITUTES: 'substitutes',
  SUBSTITUTES: 'substitutes',
  ASIN_ACCESSORY_RELATED: 'complements',
  PRODUCT_COMPLEMENTS: 'complements',
  COMPLEMENTS: 'complements',
};

export function autoTargetGroupIdFromExpression(
  expression: Array<Record<string, unknown>> | undefined
): SpAutoTargetingGroupId | null {
  for (const entry of expression ?? []) {
    const type = String(entry?.type ?? '')
      .trim()
      .toUpperCase();
    const groupId = AUTO_EXPRESSION_TO_GROUP[type];
    if (groupId) return groupId;
  }
  return null;
}

export type SpAutoTargetUpdate = {
  targetId: string;
  bid: number;
  state: 'ENABLED' | 'PAUSED';
};

/**
 * Build bid/state updates for Amazon-created AUTO targets.
 * Default mode returns [] — the ad group defaultBid is enough.
 */
export function buildSpAutoTargetUpdates(input: {
  targets: Array<{
    target_id?: string;
    expression?: Array<Record<string, unknown>>;
  }>;
  targetingMode: SpAutoTargetingMode;
  defaultBid: number;
  groups: SpAutoTargetingGroupState[];
}): SpAutoTargetUpdate[] {
  if (input.targetingMode !== 'custom') return [];

  const byId = new Map(input.groups.map((group) => [group.id, group]));
  const updates: SpAutoTargetUpdate[] = [];

  for (const target of input.targets) {
    const targetId = String(target.target_id ?? '').trim();
    if (!targetId) continue;
    const groupId = autoTargetGroupIdFromExpression(target.expression);
    if (!groupId) continue;
    const group = byId.get(groupId);
    if (!group) continue;
    const bid = group.enabled ? parseFloat(group.bid) : input.defaultBid;
    if (Number.isNaN(bid) || bid <= 0) continue;
    updates.push({
      targetId,
      bid,
      state: group.enabled ? 'ENABLED' : 'PAUSED',
    });
  }
  return updates;
}

export function buildSpNegativeKeywordPayloads(input: {
  campaignId: string;
  adGroupId: string;
  keywords: SpAddedNegativeKeyword[];
}): Array<{
  campaign_id: string;
  ad_group_id: string;
  keyword_text: string;
  match_type: SpNegativeMatchType;
  state: 'ENABLED';
}> {
  return input.keywords.map((row) => ({
    campaign_id: input.campaignId,
    ad_group_id: input.adGroupId,
    keyword_text: row.keywordText,
    match_type: row.matchType,
    state: 'ENABLED',
  }));
}
